import React, { useState, useEffect } from "react";
import RH from "../../Services/RH";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Box,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Snackbar,
  CircularProgress,
  InputAdornment,
  IconButton,
  useTheme,
  Grow,
  Divider,
  Skeleton,
  Paper,
  alpha,
  Badge,
  Tooltip,
  Zoom,
  Fade,
  AvatarGroup,
  LinearProgress
} from "@mui/material";
import ExcelSearchScreen from "./RHEXCEL";
import { useNavigate } from "react-router-dom";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Work as WorkIcon,
  Person as PersonIcon,
  Groups as TeamsIcon,
  Paid as SalaryIcon,
  Fingerprint as CINIcon,
  ContactPhone as PhoneIcon,
  AccountBox,
  Phone,
  Kitchen,
  Fingerprint,
  AssignmentInd,
  Restaurant,
  BusinessCenter,
  Support,
  Paid,
  BadgeOutlined,
  ErrorOutline as ErrorOutlineIcon,
  DeleteForever as DeleteForeverIcon,
  Email,
  Cake,
  LocationOn,
  AccessTime,
  CheckCircle,
  Warning,
  FilterList,
  Search,
  Refresh,
  Download,
  Print,
  MoreVert,
  Star,
  StarBorder,
  EmojiEmotions,
  Celebration,
  TrendingUp,
  Assessment,
  Favorite,
  LocalBar
} from "@mui/icons-material";
import MuiAlert from "@mui/material/Alert";
import { motion, AnimatePresence } from "framer-motion";

const roleGroups = {
  Management: ["Gérant", "Responsable de salle"],
  Cuisine: ["Chef de cuisine", "Cuisinier", "Aide-cuisinier"],
  Service: ["Serveur", "Barman", "Barmaid", "Hôte", "Hôtesse d'accueil"],
  Support: ["Plongeur"]
};

const roleIcons = {
  "Gérant": <BusinessCenter />,
  "Responsable de salle": <Assessment />,
  "Chef de cuisine": <Kitchen />,
  "Cuisinier": <Restaurant />,
  "Aide-cuisinier": <Restaurant />,
  "Serveur": <Restaurant />,
  "Barman": <LocalBar />,
  "Barmaid": <LocalBar />,
  "Hôte": <EmojiEmotions />,
  "Hôtesse d'accueil": <EmojiEmotions />,
  "Plongeur": <Support />
};

// Fixed status colors - using solid colors instead of gradients
const statusColors = {
  Celibataire: {
    primary: "#2196F3",
    light: "#E3F2FD",
    icon: <PersonIcon />
  },
  Marie: {
    primary: "#4CAF50",
    light: "#E8F5E9",
    icon: <Favorite />
  },
  "M+1": {
    primary: "#FF9800",
    light: "#FFF3E0",
    icon: <Celebration />
  },
  "M+2": {
    primary: "#00BCD4",
    light: "#E0F7FA",
    icon: <Celebration />
  },
  "M+3": {
    primary: "#9C27B0",
    light: "#F3E5F5",
    icon: <Celebration />
  },
  "M+4": {
    primary: "#F44336",
    light: "#FFEBEE",
    icon: <Celebration />
  }
};

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const RHPage = () => {
  const theme = useTheme();
  const restaurantId = localStorage.getItem("restaurantId");
  const [workers, setWorkers] = useState([]);
  const navigate = useNavigate();
  const [newWorker, setNewWorker] = useState({
    name: "",
    prenom: "",
    num: "",
    cin: "",
    numcnss: "",
    role: "",
    statue: "",
    salary: "",
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [stats, setStats] = useState({
    totalSalary: 0,
    averageSalary: 0,
    byRole: {},
    byStatus: {}
  });

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const fetchedWorkers = await RH.getAllWorkersByRestaurantId(restaurantId);
        setWorkers(fetchedWorkers);
        
        const total = fetchedWorkers.reduce((sum, w) => sum + Number(w.salary), 0);
        const byRole = {};
        const byStatus = {};
        
        fetchedWorkers.forEach(w => {
          byRole[w.role] = (byRole[w.role] || 0) + 1;
          byStatus[w.statue] = (byStatus[w.statue] || 0) + 1;
        });
        
        setStats({
          totalSalary: total,
          averageSalary: fetchedWorkers.length ? total / fetchedWorkers.length : 0,
          byRole,
          byStatus
        });
      } catch (error) {
        showSnackbar("Error fetching workers: " + error.message, "error");
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, [restaurantId]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddWorker = async () => {
    const requiredFields = ["name", "prenom", "num", "cin", "statue", "salary"];
    const missingFields = requiredFields.filter(field => !newWorker[field]);
    
    if (missingFields.length > 0) {
      showSnackbar(`Missing required fields: ${missingFields.join(", ")}`, "error");
      return;
    }

    try {
      const addedWorker = await RH.addWorker({ ...newWorker, restaurantId });
      setWorkers([...workers, addedWorker]);
      resetForm();
      showSnackbar("Worker added successfully!");
    } catch (error) {
      showSnackbar("Error adding worker: " + error.message, "error");
    }
  };

  const [deleteWorkerInfo, setDeleteWorkerInfo] = useState({
    open: false,
    workerId: null,
    workerName: ''
  });

  const handleDeleteWorker = async () => {
    try {
      await RH.deleteWorker(deleteWorkerInfo.workerId, restaurantId);
      setWorkers(workers.filter((worker) => worker.id !== deleteWorkerInfo.workerId));
      showSnackbar("Worker deleted successfully!");
    } catch (error) {
      showSnackbar("Error deleting worker: " + error.message, "error");
    } finally {
      setDeleteWorkerInfo({ open: false, workerId: null, workerName: '' });
    }
  };

  const resetForm = () => {
    setNewWorker({
      name: "",
      prenom: "",
      num: "",
      cin: "",
      numcnss: "",
      role: "",
      statue: "",
      salary: "",
    });
    setIsDialogOpen(false);
  };

  const filteredWorkers = workers.filter(worker => {
    const matchesSearch = searchTerm === "" || 
      `${worker.name} ${worker.prenom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.cin.includes(searchTerm);
    
    const matchesRole = filterRole === "" || worker.role === filterRole;
    const matchesStatus = filterStatus === "" || worker.statue === filterStatus;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const formatNumber = (num) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'TND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num || 0);
  };

  const toggleWorkerSelection = (workerId) => {
    setSelectedWorkers(prev =>
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  if (!restaurantId) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Zoom in={true}>
          <Paper
            sx={{
              p: 6,
              borderRadius: 4,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
              border: '1px solid #E0E0E0',
              maxWidth: 500,
              mx: 'auto'
            }}
          >
            <TeamsIcon sx={{ fontSize: 80, color: '#9AA6B2', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#1A2B3C', fontWeight: 700, mb: 1 }}>
              Aucun restaurant sélectionné
            </Typography>
            <Typography variant="body1" sx={{ color: '#5F6B7A' }}>
              Veuillez sélectionner un restaurant pour gérer votre équipe
            </Typography>
          </Paper>
        </Zoom>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      p: 4, 
      background: 'linear-gradient(145deg, #F5F7FA 0%, #E8ECF0 100%)',
      minHeight: '100vh',
      position: 'relative',
      overflowX: 'hidden'
    }}>
      {/* Background Decoration */}
      <Box sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(33,150,243,0.03) 0%, transparent 50%)',
        pointerEvents: 'none',
        zIndex: 0
      }} />

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          {/* Hero Section */}
          <motion.div variants={fadeInUp}>
            <Paper
              elevation={0}
              sx={{
                mb: 4,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,0.1)',
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(120deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0) 100%)',
                  transition: 'left 0.6s',
                },
                '&:hover::before': {
                  left: '100%'
                }
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                      <Avatar
                        sx={{
                          width: 80,
                          height: 80,
                          background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                          boxShadow: '0 10px 20px rgba(255,215,0,0.3)',
                        }}
                      >
                        <TeamsIcon sx={{ fontSize: 48, color: '#1E2A3A' }} />
                      </Avatar>
                    </Zoom>
                    <Box>
                      <Typography 
                        variant="h3" 
                        sx={{ 
                          fontWeight: 800,
                          background: 'linear-gradient(45deg, #FFFFFF 30%, #FFD700 90%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 1,
                        }}
                      >
                        Gestion d'Équipe
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 400 }}>
                        {workers.length} membres • Masse salariale: {formatNumber(stats.totalSalary)}
                      </Typography>
                    </Box>
                  </Box>
                  
                
                </Box>
              </CardContent>
            </Paper>
          </motion.div>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    borderRadius: 3,
                    background: '#FFFFFF',
                    border: `1px solid ${alpha('#667eea', 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 20px 30px ${alpha('#667eea', 0.15)}`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#667eea', 0.1), color: '#667eea' }}>
                        <TeamsIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#5F6B7A' }}>Effectif total</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                          {workers.length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    borderRadius: 3,
                    background: '#FFFFFF',
                    border: `1px solid ${alpha('#4CAF50', 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 20px 30px ${alpha('#4CAF50', 0.15)}`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}>
                        <Paid />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#5F6B7A' }}>Masse salariale</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                          {formatNumber(stats.totalSalary)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    borderRadius: 3,
                    background: '#FFFFFF',
                    border: `1px solid ${alpha('#FF9800', 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 20px 30px ${alpha('#FF9800', 0.15)}`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#FF9800', 0.1), color: '#FF9800' }}>
                        <TrendingUp />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#5F6B7A' }}>Salaire moyen</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                          {formatNumber(stats.averageSalary)}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <motion.div variants={fadeInUp}>
                <Card
                  sx={{
                    borderRadius: 3,
                    background: '#FFFFFF',
                    border: `1px solid ${alpha('#9C27B0', 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 20px 30px ${alpha('#9C27B0', 0.15)}`
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: alpha('#9C27B0', 0.1), color: '#9C27B0' }}>
                        <WorkIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#5F6B7A' }}>Postes distincts</Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                          {Object.keys(stats.byRole).length}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>

          {/* Search and Filters */}
          <motion.div variants={fadeInUp}>
            <Paper
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Rechercher un membre..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#9AA6B2' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        bgcolor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea'
                        }
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <Select
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                      displayEmpty
                      sx={{
                        bgcolor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea'
                        }
                      }}
                    >
                      <MenuItem value="">Tous les postes</MenuItem>
                      {Object.entries(roleGroups).map(([group, roles]) => [
                        <MenuItem disabled key={group} sx={{ bgcolor: '#F5F7FA' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{group}</Typography>
                        </MenuItem>,
                        ...roles.map(role => (
                          <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              {group === 'Cuisine' && <Kitchen fontSize="small" />}
                              {group === 'Service' && <Restaurant fontSize="small" />}
                              {group === 'Management' && <BusinessCenter fontSize="small" />}
                              {group === 'Support' && <Support fontSize="small" />}
                              {role}
                            </Box>
                          </MenuItem>
                        ))
                      ])}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <Select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      displayEmpty
                      sx={{
                        bgcolor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#667eea'
                        }
                      }}
                    >
                      <MenuItem value="">Tous les statuts</MenuItem>
                      {Object.keys(statusColors).map(status => (
                        <MenuItem key={status} value={status}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {statusColors[status].icon}
                            <span style={{ color: statusColors[status].primary, fontWeight: 600 }}>
                              {status}
                            </span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => setIsDialogOpen(true)}
                    startIcon={<AddIcon />}
                    sx={{
                      height: 56,
                      background: 'linear-gradient(45deg, #667eea, #764ba2)',
                      borderRadius: 2,
                      textTransform: 'none',
                      fontSize: '1rem',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 10px 20px rgba(102,126,234,0.3)'
                      }
                    }}
                  >
                    Nouveau membre
                  </Button>
                </Grid>
              </Grid>

              {/* Active Filters */}
              {(searchTerm || filterRole || filterStatus) && (
                <Fade in={true}>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {searchTerm && (
                      <Chip
                        label={`Recherche: ${searchTerm}`}
                        onDelete={() => setSearchTerm('')}
                        sx={{ bgcolor: alpha('#667eea', 0.1), color: '#667eea' }}
                      />
                    )}
                    {filterRole && (
                      <Chip
                        label={`Poste: ${filterRole}`}
                        onDelete={() => setFilterRole('')}
                        sx={{ bgcolor: alpha('#4CAF50', 0.1), color: '#4CAF50' }}
                      />
                    )}
                    {filterStatus && (
                      <Chip
                        label={`Statut: ${filterStatus}`}
                        onDelete={() => setFilterStatus('')}
                        sx={{ bgcolor: alpha('#FF9800', 0.1), color: '#FF9800' }}
                      />
                    )}
                  </Box>
                </Fade>
              )}
            </Paper>
          </motion.div>

          {/* Excel Table Section */}
          <motion.div variants={fadeInUp}>
            <Paper
              sx={{
                mb: 4,
                borderRadius: 3,
                background: '#FFFFFF',
                border: '1px solid #E0E0E0',
                overflow: 'hidden'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 600 }}>
                    Aperçu Excel
                  </Typography>
                </Box>
                <ExcelSearchScreen />
              </CardContent>
            </Paper>
          </motion.div>

          {/* Team Grid */}
          {loading ? (
            <Grid container spacing={3}>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 3 }} />
                </Grid>
              ))}
            </Grid>
          ) : filteredWorkers.length === 0 ? (
            <Fade in={true}>
              <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
                <TeamsIcon sx={{ fontSize: 80, color: '#B0BEC5', mb: 2 }} />
                <Typography variant="h5" sx={{ color: '#5F6B7A', mb: 1 }}>
                  Aucun membre trouvé
                </Typography>
                <Typography variant="body2" sx={{ color: '#9AA6B2', mb: 3 }}>
                  Essayez de modifier vos filtres ou ajoutez un nouveau membre
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setIsDialogOpen(true)}
                  sx={{ borderRadius: 2 }}
                >
                  Ajouter un membre
                </Button>
              </Paper>
            </Fade>
          ) : (
            <Grid container spacing={3}>
              <AnimatePresence>
                {filteredWorkers.map((worker, index) => (
                  <Grid item xs={12} sm={6} md={4} key={worker.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card
                        onMouseEnter={() => setHoveredCard(worker.id)}
                        onMouseLeave={() => setHoveredCard(null)}
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          background: '#FFFFFF',
                          border: `1px solid ${alpha(statusColors[worker.statue]?.primary || '#000', 0.2)}`,
                          transition: 'all 0.3s ease',
                          transform: hoveredCard === worker.id ? 'translateY(-8px)' : 'none',
                          boxShadow: hoveredCard === worker.id 
                            ? `0 20px 30px ${alpha(statusColors[worker.statue]?.primary || '#000', 0.15)}`
                            : '0 4px 12px rgba(0,0,0,0.05)',
                          position: 'relative',
                          overflow: 'visible'
                        }}
                      >
                        {/* Status Badge - Using solid color instead of gradient */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -10,
                            right: 20,
                            backgroundColor: statusColors[worker.statue]?.primary || '#000',
                            borderRadius: 2,
                            px: 2,
                            py: 0.5,
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                            zIndex: 1
                          }}
                        >
                          {worker.statue}
                        </Box>

                        <CardContent sx={{ p: 3 }}>
                          {/* Header with Avatar */}
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Badge
                              overlap="circular"
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              badgeContent={
                                <Box
                                  sx={{
                                    width: 12,
                                    height: 12,
                                    bgcolor: '#4caf50',
                                    borderRadius: '50%',
                                    border: '2px solid white'
                                  }}
                                />
                              }
                            >
                              <Avatar
                                sx={{
                                  width: 64,
                                  height: 64,
                                  backgroundColor: statusColors[worker.statue]?.primary || '#667eea',
                                  fontSize: '1.5rem',
                                  fontWeight: 600,
                                  mr: 2
                                }}
                              >
                                {worker.name?.[0]}{worker.prenom?.[0]}
                              </Avatar>
                            </Badge>
                            <Box>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                                {worker.name} {worker.prenom}
                              </Typography>
                              <Chip
                                size="small"
                                icon={roleIcons[worker.role] || <WorkIcon />}
                                label={worker.role}
                                sx={{
                                  bgcolor: alpha('#667eea', 0.1),
                                  color: '#667eea',
                                  mt: 0.5
                                }}
                              />
                            </Box>
                          </Box>

                          <Divider sx={{ my: 2 }} />

                          {/* Details Grid */}
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CINIcon sx={{ fontSize: 18, color: '#9AA6B2' }} />
                                <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                                  CIN: {worker.cin}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Phone sx={{ fontSize: 18, color: '#9AA6B2' }} />
                                <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                                  {worker.num}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Paid sx={{ fontSize: 18, color: '#9AA6B2' }} />
                                <Typography variant="body2" sx={{ color: '#5F6B7A', fontWeight: 500 }}>
                                  Salaire: <strong>{formatNumber(worker.salary)}</strong>
                                </Typography>
                              </Box>
                            </Grid>
                            {worker.numcnss && (
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <AssignmentInd sx={{ fontSize: 18, color: '#9AA6B2' }} />
                                  <Typography variant="body2" sx={{ color: '#5F6B7A' }}>
                                    CNSS: {worker.numcnss}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                          </Grid>

                          {/* Actions */}
                          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              startIcon={<InfoIcon />}
                              onClick={() => navigate(`/worker/${worker.id}`)}
                              sx={{
                                borderRadius: 2,
                                background: 'linear-gradient(45deg, #667eea, #764ba2)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: theme.shadows[4]
                                }
                              }}
                            >
                              Détails
                            </Button>
                            <Tooltip title="Supprimer">
                              <IconButton
                                onClick={() => setDeleteWorkerInfo({
                                  open: true,
                                  workerId: worker.id,
                                  workerName: `${worker.name} ${worker.prenom}`
                                })}
                                sx={{
                                  bgcolor: alpha('#f44336', 0.1),
                                  color: '#f44336',
                                  '&:hover': {
                                    bgcolor: alpha('#f44336', 0.2),
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </AnimatePresence>
            </Grid>
          )}

          {/* Footer Stats */}
          <motion.div variants={fadeInUp}>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                {filteredWorkers.length} membres affichés sur {workers.length} total
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Chip
                  icon={<CheckCircle />}
                  label={`${Object.keys(stats.byRole).length} postes`}
                  size="small"
                  sx={{ bgcolor: '#F5F7FA', color: '#5F6B7A' }}
                />
                <Chip
                  icon={<Star />}
                  label={`${Object.keys(stats.byStatus).length} statuts`}
                  size="small"
                  sx={{ bgcolor: '#F5F7FA', color: '#5F6B7A' }}
                />
              </Box>
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      {/* Add Worker Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={resetForm}
        fullWidth
        maxWidth="md"
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: '#FFFFFF',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            py: 3,
            position: 'relative',
            '&:before': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: 4,
              background: 'linear-gradient(90deg, #FFD700, #FFA500)'
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
              <AddIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Ajouter un nouveau membre
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Remplissez les informations ci-dessous pour ajouter un membre à l'équipe
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 4, px: 3 }}>
          <br></br>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={newWorker.name}
                onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeOutlined sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                value={newWorker.prenom}
                onChange={(e) => setNewWorker({ ...newWorker, prenom: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccountBox sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                value={newWorker.num}
                onChange={(e) => setNewWorker({ ...newWorker, num: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CIN"
                value={newWorker.cin}
                onChange={(e) => setNewWorker({ ...newWorker, cin: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Fingerprint sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Numéro CNSS"
                value={newWorker.numcnss}
                onChange={(e) => setNewWorker({ ...newWorker, numcnss: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AssignmentInd sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Poste</InputLabel>
                <Select
                  value={newWorker.role}
                  onChange={(e) => setNewWorker({ ...newWorker, role: e.target.value })}
                  label="Poste"
                >
                  {Object.entries(roleGroups).map(([group, roles]) => [
                    <MenuItem disabled key={group} sx={{ bgcolor: '#F5F7FA' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{group}</Typography>
                    </MenuItem>,
                    ...roles.map(role => (
                      <MenuItem key={role} value={role} sx={{ pl: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {group === 'Cuisine' && <Kitchen fontSize="small" />}
                          {group === 'Service' && <Restaurant fontSize="small" />}
                          {group === 'Management' && <BusinessCenter fontSize="small" />}
                          {group === 'Support' && <Support fontSize="small" />}
                          {role}
                        </Box>
                      </MenuItem>
                    ))
                  ])}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Situation familiale</InputLabel>
                <Select
                  value={newWorker.statue}
                  onChange={(e) => setNewWorker({ ...newWorker, statue: e.target.value })}
                  label="Situation familiale"
                >
                  {Object.keys(statusColors).map((status) => (
                    <MenuItem key={status} value={status}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {statusColors[status].icon}
                        <span style={{ color: statusColors[status].primary, fontWeight: 600 }}>
                          {status}
                        </span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Salaire mensuel (TND)"
                type="number"
                value={newWorker.salary}
                onChange={(e) => setNewWorker({ ...newWorker, salary: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Paid sx={{ color: '#667eea' }} />
                    </InputAdornment>
                  ),
                  endAdornment: <InputAdornment position="end">TND</InputAdornment>
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#F5F7FA' }}>
          <Button
            onClick={resetForm}
            variant="outlined"
            sx={{ borderRadius: 2, px: 4 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleAddWorker}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              borderRadius: 2,
              px: 4,
              background: 'linear-gradient(45deg, #667eea, #764ba2)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 20px rgba(102,126,234,0.3)'
              }
            }}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteWorkerInfo.open}
        onClose={() => setDeleteWorkerInfo({ ...deleteWorkerInfo, open: false })}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: 400,
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle sx={{ pt: 4 }}>
          <ErrorOutlineIcon sx={{ fontSize: 60, color: '#f44336', mb: 2 }} />
          <Typography variant="h5" fontWeight={700}>
            Confirmer la suppression
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ color: '#5F6B7A' }}>
            Êtes-vous sûr de vouloir supprimer définitivement
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1 }}>
            {deleteWorkerInfo.workerName}
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontStyle: 'italic' }}>
            Cette action est irréversible
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 4, pt: 2 }}>
          <Button
            onClick={() => setDeleteWorkerInfo({ ...deleteWorkerInfo, open: false })}
            variant="outlined"
            sx={{ borderRadius: 2, px: 4, mr: 2 }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteWorker}
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RHPage;